import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from './redux';
import { logoutUser, setError } from '../store/authSlice';
import { config } from '../config';

/**
 * Hook to monitor subscription status while user is logged in
 * Checks every 5 minutes and logs user out if subscription expires
 */
export const useSubscriptionMonitor = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, isAdmin, token } = useAppSelector((state) => state.auth);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Only monitor for authenticated non-admin users
    if (!isAuthenticated || isAdmin) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Only these explicit error codes from the backend should trigger a
    // forced logout. Anything else (transient 5xx, an unexpected 401 with no
    // recognized code) should be treated like a network error: log a warning
    // and let the next 5-minute poll re-check.
    const LOGOUT_ERROR_CODES: Record<string, string> = {
      NO_SUBSCRIPTION:
        'No active subscription found. You have been logged out. Please contact your administrator to create a subscription.',
      ORGANIZATION_NOT_FOUND:
        'Organization not found. You have been logged out. Please contact your administrator.',
      SUBSCRIPTION_EXPIRED:
        'Your subscription has expired. You have been logged out. Please contact your administrator to renew.',
      SUBSCRIPTION_GRACE_PERIOD_ENDED:
        'Your subscription grace period has ended. You have been logged out. Please contact your administrator.',
      USER_DISABLED:
        "Your access has been revoked by the administrator. You have been logged out. If you think it's a mistake, please contact support.",
    };

    // Function to check subscription status
    const checkSubscriptionStatus = async () => {
      try {
        const apiUrl = config.server.backendUrl || 'http://localhost:8000';
        const response = await fetch(`${apiUrl}/api/admin/verify-access`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          console.debug('[SubscriptionMonitor] Subscription status check passed');
          return;
        }

        let errorCode = '';
        let errorMessage = '';
        try {
          const errorData = await response.json();
          errorCode = errorData.error || '';
          errorMessage = errorData.message || '';
        } catch (parseError) {
          console.warn(
            '[SubscriptionMonitor] Could not parse error body; treating as transient',
            parseError
          );
          return;
        }

        const userMessage = LOGOUT_ERROR_CODES[errorCode];
        if (!userMessage) {
          // Unknown error — likely a transient backend issue. Don't log out.
          console.warn(
            '[SubscriptionMonitor] Non-logout error from verify-access; will retry next cycle:',
            { status: response.status, errorCode, errorMessage }
          );
          return;
        }

        console.warn('[SubscriptionMonitor] Access verification failed:', errorCode);
        dispatch(setError(userMessage));
        console.log('[SubscriptionMonitor] Logging user out due to subscription/access issue');
        dispatch(logoutUser());
      } catch (error: any) {
        console.error('[SubscriptionMonitor] Error checking subscription status:', error);
        // Don't log user out on network errors - only on explicit access denial
      }
    };

    // Initial check after 1 minute (to avoid immediate check on login)
    const initialTimeout = setTimeout(() => {
      checkSubscriptionStatus();
    }, 60 * 1000); // 1 minute

    // Set up interval to check every 5 minutes
    intervalRef.current = setInterval(() => {
      checkSubscriptionStatus();
    }, 5 * 60 * 1000); // 5 minutes

    console.log('[SubscriptionMonitor] Started monitoring subscription status');

    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      clearTimeout(initialTimeout);
      console.log('[SubscriptionMonitor] Stopped monitoring subscription status');
    };
  }, [isAuthenticated, isAdmin, token, dispatch]);
};
